import clsx from 'clsx';
import { ArrowDown, ArrowUp, Plus, Trash2, X } from 'lucide-react';
import { useRef, type ReactNode } from 'react';
import type { UserRef } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatNumber } from '../lib/format';
import { Button, Select } from './ui';

/**
 * A workflow built on the task itself (operator, 2026-10-15: "i want remove
 * workflow as sprite tab i want to build this workflow as is it but in the task
 * it self"): people in levels, as the Workflows tab's form had them. Level 1 gets
 * the task first; people on one level work on it at the same time, and the next
 * level starts once they have all finished. The New task form, Assign, converting
 * a change request and handing on a Leaders Task each build one here — and Edit
 * the workflow changes the levels a running one has not reached yet, numbered on
 * from them.
 */

export interface LevelDraft {
  /** Stable across reordering, so React keeps each level's picker with it. */
  key: number;
  userIds: string[];
}

let nextKey = 1;
/** A level as the builder holds it — of these people, or of nobody yet. */
export const levelOf = (userIds: string[]): LevelDraft => ({ key: nextKey++, userIds });
export const blankLevel = (): LevelDraft => levelOf([]);
export const startLevels = (): LevelDraft[] => [blankLevel()];

/** At least one level, and someone on each. */
export function levelsReady(levels: LevelDraft[]): boolean {
  return levels.length > 0 && levels.every((level) => level.userIds.length > 0);
}

/** What the API takes: `{ levels: [{ userIds }] }`, level 1 first. */
export function workflowPayload(levels: LevelDraft[]) {
  return { levels: levels.map((level) => ({ userIds: level.userIds })) };
}

/** Everyone on the levels, once each, in the order they come. */
export function everyoneOnLevels(levels: LevelDraft[]): string[] {
  return [...new Set(levels.flatMap((level) => level.userIds))];
}

export function WorkflowBuilder({
  levels,
  onChange,
  people,
  known,
  firstLevel = 1,
  minLevels = 1,
  legend,
  hint,
  flagged,
  flagLabel,
  flagTone = 'error',
  disabled,
  footer,
}: {
  levels: LevelDraft[];
  onChange: (levels: LevelDraft[]) => void;
  /** Who may be put on it: the people the task could go to. */
  people: UserRef[];
  /** People on it already who may not be offered — a running workflow's — named all the same. */
  known?: UserRef[];
  /** The first level's number: a running workflow's levels ahead go on from the ones reached. */
  firstLevel?: number;
  /** How few levels it may have: none at all, for the levels ahead of a running workflow. */
  minLevels?: number;
  legend?: ReactNode;
  hint?: ReactNode;
  /** People already on it who should not be — off the project, say — marked, with `flagLabel` as the reason. */
  flagged?: ReadonlySet<string>;
  flagLabel?: string;
  /** `warning` marks someone who may stay — no longer active, say — rather than someone who must go. */
  flagTone?: 'error' | 'warning';
  disabled?: boolean;
  /** Below the levels: who gets what, or why it cannot be saved. */
  footer?: ReactNode;
}) {
  const { t, locale, localized } = useI18n();
  // Names for everyone ever offered — someone picked stays named when the list changes under them.
  const names = useRef(new Map<string, UserRef>());
  for (const p of [...(known ?? []), ...people]) names.current.set(p.id, p);

  const changeLevel = (key: number, userIds: string[]) =>
    onChange(levels.map((level) => (level.key === key ? { ...level, userIds } : level)));
  const move = (index: number, by: -1 | 1) => {
    const next = [...levels];
    [next[index], next[index + by]] = [next[index + by], next[index]];
    onChange(next);
  };

  return (
    <fieldset disabled={disabled}>
      <legend className="text-body-sm font-medium text-[var(--text-secondary)]">
        {legend ?? t('workflows.levels')} {minLevels > 0 && <span className="text-primary dark:text-primary-400">*</span>}
      </legend>
      <p className="mb-3 mt-1 text-caption text-faint">{hint ?? t('workflows.levelsHint')}</p>

      <ol className="space-y-3">
        {levels.map((level, index) => {
          const n = formatNumber(firstLevel + index, locale);
          const pickable = people.filter((p) => !level.userIds.includes(p.id));
          return (
            <li
              key={level.key}
              className={clsx(
                'rounded-md border p-3 transition-colors duration-fast',
                level.userIds.length ? 'hairline' : 'border-warning/35',
              )}
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-body-sm font-semibold text-[var(--text-primary)]">
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-primary/15 text-caption text-primary-600 dark:text-primary-400 tabular">
                    {n}
                  </span>
                  {t('workflows.levelN', { n })}
                </span>
                <span className="flex items-center gap-0.5">
                  <IconButton label={t('workflows.moveUp')} disabled={disabled || index === 0} onClick={() => move(index, -1)}>
                    <ArrowUp className="h-4 w-4" />
                  </IconButton>
                  <IconButton
                    label={t('workflows.moveDown')}
                    disabled={disabled || index === levels.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </IconButton>
                  <IconButton
                    label={t('workflows.removeLevel')}
                    disabled={disabled || levels.length <= minLevels}
                    onClick={() => onChange(levels.filter((l) => l.key !== level.key))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </IconButton>
                </span>
              </div>

              {level.userIds.length > 0 && (
                <ul className="mb-2 flex flex-wrap gap-1.5">
                  {level.userIds.map((userId) => {
                    const p = names.current.get(userId);
                    const name = p ? localized(p) : '—';
                    const off = !!flagged?.has(userId);
                    return (
                      <li
                        key={userId}
                        title={off ? flagLabel : undefined}
                        className={clsx(
                          'inline-flex items-center gap-1 rounded-pill py-1 pe-1 ps-2.5 text-body-sm ring-1 ring-inset',
                          !off
                            ? 'bg-primary/12 text-[var(--text-primary)] ring-primary/30'
                            : flagTone === 'warning'
                              ? 'bg-warning-bg text-warning-fg ring-warning/30 dark:bg-warning/14 dark:text-warning-dark'
                              : 'bg-error/10 text-error-fg dark:text-error-dark ring-error/35',
                        )}
                      >
                        {name}
                        {off && flagLabel && <span className="text-caption">· {flagLabel}</span>}
                        <button
                          type="button"
                          onClick={() => changeLevel(level.key, level.userIds.filter((u) => u !== userId))}
                          className="rounded-full p-0.5 text-[var(--text-tertiary)] hover:bg-white/10 hover:text-[var(--text-primary)]"
                          aria-label={t('workflows.removePerson', { name })}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              <Select
                value=""
                aria-label={`${t('workflows.levelN', { n })} — ${t('workflows.addPerson')}`}
                onChange={(e) => e.target.value && changeLevel(level.key, [...level.userIds, e.target.value])}
              >
                <option value="">{t('workflows.addPerson')}</option>
                {pickable.map((p) => (
                  <option key={p.id} value={p.id}>
                    {localized(p)}
                  </option>
                ))}
              </Select>
              <p className="mt-1.5 text-caption text-faint">
                {level.userIds.length === 0
                  ? t('workflows.levelEmpty')
                  : level.userIds.length > 1
                    ? t('workflows.together')
                    : null}
              </p>
            </li>
          );
        })}
      </ol>

      <Button
        className="mt-3"
        size="sm"
        icon={<Plus className="h-4 w-4" />}
        disabled={disabled}
        onClick={() => onChange([...levels, blankLevel()])}
      >
        {t('workflows.addLevel')}
      </Button>
      {footer}
    </fieldset>
  );
}

function IconButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="rounded-sm p-1.5 text-[var(--text-tertiary)] transition-colors duration-fast hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)] disabled:cursor-not-allowed disabled:opacity-35"
    >
      {children}
    </button>
  );
}
